<script setup>
import { computed } from 'vue';

import { HrIconBtn } from '../primitives';

/**
 * Icon button for destructive actions such as delete or remove.
 *
 * @status recommended
 * @group actions
 * @useWhen The action permanently removes or irreversibly modifies data
 * @avoidWhen The action is not destructive — use HrIconActionBtn
 * @related HrIconActionBtn
 * @related HrIconMenuBtn
 * @a11y Icon-only — callers must supply aria-label that clearly names the destructive action.
 */
defineOptions({ name: 'HrIconDestructiveBtn' });

const emit = defineEmits({
  /** Fired when the user activates the button. */
  click: null,
});

const props = defineProps({
  /**
   * Material icon name.
   * @example delete
   */
  icon: { type: String, required: true },
  /** Shows a loading spinner in place of the icon. */
  loading: { type: Boolean, default: false },
  /** Prevents interaction and applies a dimmed appearance. */
  disable: { type: Boolean, default: false },
  /** Renders a skeleton placeholder instead of the button. */
  pending: { type: Boolean, default: false },
  /** Vue Router route location. Renders the button as a router-link. */
  to: { type: [String, Object], default: null },
  /** Replace the current history entry instead of pushing a new one. */
  replace: { type: Boolean, default: false },
  /** Native anchor href. Use for external links; prefer `to` for in-app navigation. */
  href: { type: String, default: null },
  /** Native anchor target (e.g. `_blank`). Only meaningful when `href` is set. */
  target: { type: String, default: null },
  /**
   * Quasar visual props forwarded to the underlying icon button. Ignored in the design-system theme.
   * @deprecated Will be removed when the legacy theme is removed.
   */
  legacyProps: { type: Object, default: () => ({}) },
});

// @todo Remove legacy-props when Legacy theme is removed
const mergedLegacyProps = computed(() => ({
  flat: true,
  color: 'negative',
  ...props.legacyProps,
}));
</script>

<template>
  <hr-icon-btn
    variant="standard"
    destructive
    v-bind="$props"
    :legacy-props="mergedLegacyProps"
    @click="emit('click', $event)"
  >
    <!-- @slot Overlay content placed inside the button — e.g. a tooltip or badge. -->
    <slot />
  </hr-icon-btn>
</template>
